import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChartAxes } from './ChartAxes';
import { jascaydTrajectory, placeboTrajectory, results } from '../../data/study';
import { useChartGeometry } from '../../hooks/useChartGeometry';
import { formatMl, WEEKS } from '../../utils/chart';
import { EASE } from '../../utils/motion';
import { displayFont, palette } from '../../utils/palette';
type TrajectoryChartProps = {
  animated?: boolean;
  prediction?: number | null;
  showGapLabel?: boolean;
  className?: string;
};
export function TrajectoryChart({
  animated = true,
  prediction = null,
  showGapLabel = false,
  className = ''
}: TrajectoryChartProps) {
  const reduce = useReducedMotion();
  const geo = useChartGeometry();
  const {
    box,
    x,
    y,
    compact
  } = geo;
  const play = animated && !reduce;
  const tr = (delay: number, duration = 0.3) => ({
    duration: play ? duration : 0,
    delay: play ? delay : 0,
    ease: EASE
  });
  const endX = x(WEEKS);
  const yP = y(results.placebo);
  const yJ = y(results.jascayd);
  const yGuess = prediction !== null ? y(results.placebo + prediction) : null;
  const labelX = endX + (compact ? 18 : 26);
  const nameSize = compact ? 10 : 12;
  const valueSize = compact ? 17 : 24;
  const gapLabelY = (y(-90) + y(-142)) / 2;
  return <svg viewBox={`0 0 ${box.width} ${box.height}`} className={`h-auto w-full overflow-visible ${className}`} role="img" aria-label={`Change in FVC from baseline to Week 52. Placebo ${formatMl(results.placebo)} mL. JASCAYD 18 mg ${formatMl(results.jascayd)} mL. Adjusted difference ${results.difference} mL.`} data-id="element-24" data-mp="20bm0g5hx370t">
      <ChartAxes geo={geo} data-id="element-25" data-mp="2j120ystlqzh8" />

      <motion.path d={geo.area(jascaydTrajectory, placeboTrajectory)} fill={palette.accentSoft} initial={{
      opacity: play ? 0 : 1
    }} animate={{
      opacity: 1
    }} transition={tr(1.55)} data-id="element-26" data-mp="2j093yjkg3oca" />

      <motion.path d={geo.smooth(placeboTrajectory)} fill="none" stroke={palette.placebo} strokeWidth={2.5} strokeLinecap="round" initial={{
      pathLength: play ? 0 : 1
    }} animate={{
      pathLength: 1
    }} transition={tr(0.15)} data-id="element-27" data-mp="17ngdrd7az7yc" />
      <motion.path d={geo.smooth(jascaydTrajectory)} fill="none" stroke={palette.accent} strokeWidth={3.5} strokeLinecap="round" initial={{
      pathLength: play ? 0 : 1
    }} animate={{
      pathLength: 1
    }} transition={tr(0.7)} data-id="element-28" data-mp="eq5tukxqnj53" />

      <circle cx={x(0)} cy={y(0)} r={4} fill={palette.ink} data-id="element-29" data-mp="3vyj01aqdvouu" />
      <motion.circle cx={endX} cy={yP} r={4.5} fill={palette.placebo} initial={{
      opacity: play ? 0 : 1
    }} animate={{
      opacity: 1
    }} transition={tr(0.45, 0.2)} data-id="element-30" data-mp="3rxz7kkgr99f1" />
      <motion.circle cx={endX} cy={yJ} r={5.5} fill={palette.accent} initial={{
      opacity: play ? 0 : 1
    }} animate={{
      opacity: 1
    }} transition={tr(1.0, 0.2)} data-id="element-31" data-mp="3n0guqizmaa1r" />

      <motion.g initial={{
      opacity: play ? 0 : 1
    }} animate={{
      opacity: 1
    }} transition={tr(1.7)} data-id="element-32" data-mp="1l6zrdriirvct">
        <line x1={endX + 10} x2={endX + 10} y1={yJ + 8} y2={yP - 8} stroke={palette.accent} strokeWidth={1.25} data-id="element-33" data-mp="1srjyp3xsi7l2" />
        <line x1={endX + 6} x2={endX + 14} y1={yJ + 8} y2={yJ + 8} stroke={palette.accent} strokeWidth={1.25} data-id="element-34" data-mp="1xp3foblvgygp" />
        <line x1={endX + 6} x2={endX + 14} y1={yP - 8} y2={yP - 8} stroke={palette.accent} strokeWidth={1.25} data-id="element-35" data-mp="22i698jqng7ww" />
      </motion.g>

      <motion.g initial={{
      opacity: play ? 0 : 1
    }} animate={{
      opacity: 1
    }} transition={tr(0.5)} data-id="element-36" data-mp="27i104nafcxhy">
        <text x={labelX} y={yP - 6} fill={palette.muted} fontSize={nameSize} letterSpacing="0.06em" data-id="element-37" data-mp="19uajjyzhzg9m">
          PLACEBO
        </text>
        <text x={labelX} y={yP + valueSize - 4} fill={palette.ink} fontSize={valueSize} fontFamily={displayFont} style={{
        fontVariantNumeric: 'tabular-nums'
      }} data-id="element-38" data-mp="1jpubkaaxxut3">
          {formatMl(results.placebo)} mL
        </text>
      </motion.g>
      <motion.g initial={{
      opacity: play ? 0 : 1
    }} animate={{
      opacity: 1
    }} transition={tr(1.05)} data-id="element-39" data-mp="2v4r2xqulkw3m">
        <text x={labelX} y={yJ - 6} fill={palette.accent} fontSize={nameSize} letterSpacing="0.06em" fontWeight={600} data-id="element-40" data-mp="2z5t1e5gox44j">
          JASCAYD 18 mg
        </text>
        <text x={labelX} y={yJ + valueSize - 4} fill={palette.accent} fontSize={valueSize} fontFamily={displayFont} style={{
        fontVariantNumeric: 'tabular-nums'
      }} data-id="element-41" data-mp="2sr4136ltlukf">
          {formatMl(results.jascayd)} mL
        </text>
      </motion.g>

      {showGapLabel && <text x={x(40)} y={gapLabelY} dy="0.35em" textAnchor="middle" fill={palette.accent} fontSize={compact ? 12 : 14} fontWeight={600} style={{
      fontVariantNumeric: 'tabular-nums'
    }} data-id="element-42" data-mp="32r4nx2iwestd">
          {results.difference} mL
        </text>}

      {yGuess !== null && play && <motion.g initial={{
      opacity: 0,
      y: 0
    }} animate={{
      opacity: [0, 1, 1, 0],
      y: [0, 0, 0, yJ - yGuess]
    }} transition={{
      duration: 1.15,
      delay: 0.45,
      times: [0, 0.18, 0.74, 1],
      ease: EASE
    }} data-id="element-43" data-mp="3lguhjmaxd8jp">
          <line x1={endX - (compact ? 44 : 70)} x2={endX - 8} y1={yGuess} y2={yGuess} stroke={palette.ink} strokeWidth={1} strokeDasharray="3 3" data-id="element-44" data-mp="3vbw5ietgs4kp" />
          <circle cx={endX} cy={yGuess} r={6.5} fill={palette.paper} stroke={palette.ink} strokeWidth={1.5} data-id="element-45" data-mp="mvydv9ut8oo6" />
          <text x={endX - (compact ? 50 : 76)} y={yGuess} dy="0.32em" textAnchor="end" fill={palette.ink} fontSize={compact ? 10.5 : 12} data-id="element-46" data-mp="8uvvp86xryij">
            Your estimate
          </text>
        </motion.g>}
    </svg>;
}